import { CardElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { Stripe } from '@stripe/stripe-js';
import React, { useEffect, useState } from 'react';
import {Button, Spinner, Stack} from 'react-bootstrap';
import {
  useCreateInvoiceClientPaymentMutation,
  useCreateInvoiceIntentMutation,
  useCreatePaymentMutation,
  useGetPaymentIntentMutation
} from '../../../../../services/endpoints/finance/stripe';
interface StripeProps {
  stripePromise: Promise<Stripe | null> | null;
  locationId: number;
  invoice_id: number;
  amount: string;
  access_code?: string;
}

// Componente hijo para manejar la lógica del formulario de pago
const PaymentForm: React.FC<{ clientSecret: string; invoice_id: number; locationId: number; access_code?: string }> = ({
                                                                                                                         clientSecret,
                                                                                                                         invoice_id,
                                                                                                                         locationId,
                                                                                                                         access_code
                                                                                                                       }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [createInvoiceClientPayment] = useCreateInvoiceClientPaymentMutation();
  const paymentMethodType = 'credit_card';
  const [submitting, setSubmitting] = useState(false);

  const handlePayment = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      if (!stripe || !elements) {
        setError('Stripe has not loaded yet.');
        return;
      }

      const cardElement = elements.getElement(CardElement);

      if (!cardElement) {
        setError('Card element is not found.');
        return;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement
      });

      if (error || !paymentMethod) {
        setError(error?.message || 'An error occurred.');
        return;
      }

      const { error: paymentError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: paymentMethod.id
      });

      if (paymentError) {
        setError(paymentError.message || 'Payment failed.');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Public invoice flow: use the pi_ id as the transaction reference.
        // We do NOT call get-payment-intent (admin-only). Recording is idempotent
        // on transaction_id server-side, so a retry cannot double-record.
        const chargeId: string = paymentIntent.id;
        const cardLast4 = paymentMethod.card?.last4;
        if (!cardLast4) { setError('Could not read card details. Please contact support.'); return; }
        try {
          await createInvoiceClientPayment({
            invoice_id,
            payment_method: paymentMethodType,
            total: paymentIntent.amount / 100,
            notes: '',
            transaction_id: chargeId,
            cc_last_4: cardLast4,
            access_code
          }).unwrap();
          setError(null);
          alert('Payment successful!');
        } catch (recordErr) {
          setError('Your payment went through but we could not record it. Please do NOT retry — contact support to confirm your payment.');
        }
      } else {
        setError('Payment failed or status unknown.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
      <form id="invoice-payment-form">
        <Stack gap={3}>
          <div>
            <label className="form-label fw-semibold text-secondary mb-2">
              Card Details
            </label>
            <div
                className="p-3"
                style={{
                  border: '1px solid rgba(94, 100, 105, 0.35)',
                  borderRadius: 8,
                  background: '#fff'
                }}
            >
              <CardElement
                  options={{
                    style: {
                      base: {
                        fontSize: '16px',
                        color: '#32325d',
                        fontFamily: 'inherit',
                        '::placeholder': { color: '#8c9196' }
                      },
                      invalid: { color: '#dc3545' }
                    }
                  }}
              />
            </div>
          </div>
          {error && (
              <div className="text-danger" role="alert">
                {error}
              </div>
          )}
          <Button
              variant="primary"
              className="text-uppercase w-100"
              onClick={handlePayment}
              disabled={submitting}
          >
            {submitting ? (
                <Spinner animation="border" size="sm" />
            ) : (
                'Pay Now'
            )}
          </Button>
        </Stack>
      </form>
  );
};

// Componente principal que envuelve con `Elements`
const InvoicesViewForm: React.FC<StripeProps> = ({
  stripePromise,
  locationId,
  invoice_id,
  amount,
  access_code
}) => {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [createStripeIntent] = useCreateInvoiceIntentMutation();

  useEffect(() => {
    if (stripePromise) {
      stripePromise.then((resolvedStripe) => setStripe(resolvedStripe));
    }
  }, [stripePromise]);

  useEffect(() => {
    const fetchClientSecret = async () => {
      try {
        const response = await createStripeIntent({
          location_id: Number(locationId),
          invoice_id,
          amount: parseFloat(amount),
          access_code
        }).unwrap();

        setClientSecret(response.clientSecret);
      } catch (error) {
        console.error('Error fetching client secret:', error);
      }
    };

    fetchClientSecret();
  }, [createStripeIntent, locationId, invoice_id, amount, access_code]);

  if (!stripePromise) {
    return <span>Failed to load Stripe</span>;
  }

  if (!stripe || !clientSecret) {
    return <span>Loading Stripe...</span>;
  }

  return (
    <Elements stripe={stripe}>
      <PaymentForm
          clientSecret={clientSecret}
          invoice_id={invoice_id}
          locationId={locationId}
          access_code={access_code}
      />
    </Elements>
  );
};

export default InvoicesViewForm;
